import { DataObject, DataObjectFactory } from "@/v2/models/base-data-object.js";
import { CompanyFactory, CompanyMapperParameter } from "@/v2/models/companies.js";
import { MediaFactory, MediaMapperParameter } from "@/v2/models/medias.js";
import { Parameter } from "@/v2/models/parameters";
import { ProductFactory, ProductMapperParameter } from "@/v2/models/products.js";
import { TaxClassFactory, TaxClassMapperParameter } from "@/v2/models/tax-classes.js";
import { WarehouseFactory, WarehouseMapperParameter } from "@/v2/models/warehouses.js";
import { now } from "@/v2/utils/date.js";
import moment from "moment";

export class Document extends DataObject {
    _type = "document";

    id;
    id_link;
    term;
    number;
    temporary_document_number;
    external_number;
    type;
    status;
    credit_approval_status;
    credit_approval_workflow;
    total;
    billing_address;
    shipping_address;
    company;
    line_items = {};
    custom_fields;
    date;
    attachments = {};
    to_location;
    created_at;
    updated_at;
    date_valid_from;
    date_valid_to;
    custom_field_attachments;
    payment_method;
    payment_method_label;
    lending_program_name;
    total_points;
    remark;
    agent_code;
    agent_name;
    transfer_status;
    created_by;
    fulfiller = {};
    parcels = {};

    payment_amount;
    payment_remark;
    document_paying_fors;
    document_paid_withs;
    document_paying_withs;
    document_used_to_pay;
    payment_method;
    payment_method_label;
    cheque_date;
    cheque_number;
    site_name;
    from_location;
    to_location;

    outstanding;
    paid = 0;
    discount = 0;

    amount;
    unapplied_amount;
    used = 0;
    delivery_date;
    shipping_line;
    coupon_logs = {};
    discounts = [];
    split_group;
    allow_multiple_split_groups;
    default_incoterm_type;
    default_incoterm_from;
    default_incoterm_to;
    error_user_credit_limit_exceeded_warn_message; // show warning if credit_limit exceeded
    credit_term_code;

    packed_at;
    packed_by = {};
    packed_by_name = null;

    price_approval_status;
    price_approval_status_slug;
    e_invoice_uuid;
    e_invoice_code;
    merged_documents = [];
    e_invoices = {};
    currency;
    currencySymbol;

    errors = {};

    due_date;
    overdue_date;
    last_payment_date;
    payment_status;
    payment_timeline;

    delivered_at;
    delivered_by;
    delivered_by_name;
    is_delivered;

    phone_verification_status;
    epod_bypass_remark;
    received_at;
    received_by;
    received_by_identification_number;
    received_by_phone;
    received_at_longitude;
    received_at_latitude;
    proof_of_delivery = {};
    received_by_e_signature = {};

    integration;
    external_account_code;

    reference_documents = {};
    document_referenced;
    document_error_messages;

    // AI Document
    aidoc_template;
    aidoc_attachment;

    // terms and conditions
    document_terms_and_conditions;

    // purchase limit
    purchase_limit_approval_status;

    getDueDate() {
        const dueDate = moment(this.date).add(this.term, "d").toDate();
        return dueDate;
    }

    getDocumentStatus() {
        return this.status;
    }

    getDocumentDate() {
        return this.date ?? this.created_at;
    }
}

export class InvoiceDocument extends Document {
    _type = "invoice_document";

    getDocumentStatus() {
        if (this.outstanding == 0) {
            return "paid";
        }

        const currentDate = now();
        if (currentDate > this.getDueDate()) {
            return "overdue";
        } else {
            return "outstanding";
        }
    }
}

export class CreditNoteDocument extends Document {
    _type = "credit_note_document";

    getDocumentStatus() {
        if (this.unapplied_amount == 0) {
            return "used";
        } else {
            return "unapplied";
        }
    }
}

export class PaymentDocument extends Document {
    _type = "payment_document";
}
export class PurchaseInvoiceDocument extends Document {
    _type = "purchase_invoice_document";
}
export class PurchaseCreditNoteDocument extends Document {
    _type = "purchase_credit_note_document";
}

export class CashSaleDocument extends Document {
    _type = "cash_sale_document";
}

export class DeliveryOrderDocument extends Document {
    _type = "delivery_order_document";
}

export class LoanInvoiceDocument extends Document {
    _type = "loan_invoice_document";
}

export class LoanPaymentDocument extends Document {
    _type = "loan_payment";
}

export class QuotationDocument extends Document {
    _type = "quotation_document";
}

export class SalesOrderDocument extends Document {
    _type = "sales_order_document";
}

export class TransferNoteDocument extends Document {
    _type = "transfer_note_document";
}

export class TransferRequestDocument extends Document {
    _type = "transfer_request_document";
}

export class PurchaseOrderDocument extends Document {
    _type = "purchase_order_document";
}

export class SubmissionDocument extends Document {
    _type = "submission_document";
}

export class WarrantyDocument extends Document {
    _type = "warranty_document";
}

export class PickListDocument extends Document {
    _type = "pick_list_document";
}

export class ProformaInvoiceDocument extends Document {
    _type = "proforma_invoice_document";

    getDocumentStatus() {
        if (this.outstanding == 0) {
            return "paid";
        } else {
            return "outstanding";
        }
    }
}

export class GoodsReceivedDocument extends Document {
    _type = "goods_received_document";
}

export class StockIssueDocument extends Document {
    _type = "stock_issue_document";
}

export class StockReceiveDocument extends Document {
    _type = "stock_receive_document";
}

export class StockItemAssemblyDocument extends Document {
    _type = "stock_item_assembly_document";
}
export class DebitNoteDocument extends Document {
    _type = "debit_note_document";
}

export class DocumentLineItem extends DataObject {
    _type = "document_line_item";

    id;
    sequence;
    bo_can_delete;
    show_action_menu;
    document_number;
    document_type;
    document_status;
    admin_portal_document_id_link;
    subtotal;
    subtotal_tax;
    total;
    total_tax;
    price;
    quantity;
    untransferred_quantity;
    request_quantity = 0;
    product_name;
    product_sku;
    variations;
    product_id;
    product;
    discounts = [];
    total_points;
    composite_parent;
    composite_children = [];
    remark;
    from_location;
    to_location;
    transferred_from;
    related_document_transferred_to = {};
    transferred_to = {};
    return_reason;
    batch;
    serial_number = [];
    untransferred_serial_number = [];
    created_at;
    total_tax;
    tax_class;
    tax_rate;
    shipping_address;
    discounts;
    transfer_status;
    meta;
    document_date;
    document_id_link;
    default_incoterm_type;
    default_incoterm_from;
    default_incoterm_to;

    display_subtotal;
    display_total;

    price_approval_status;
    price_approval_status_slug;

    min_price;
    system_price;
    external_price;
    related_line_items = {};

    integration;
    external_sku;
    external_uom;

    total_received_quantity;
    total_shipped_quantity;

    purchase_limit_approval_status;
    shipping_class;

    getUnitPrice() {
        if (this.subtotal === null || this.quantity === null) {
            return null;
        }

        if (this.quantity == 0) {
            return this.quantity;
        }

        return this.subtotal / this.quantity;
    }

    getEffectiveUnitPrice() {
        if (this.total === null || this.quantity === null) {
            return null;
        }

        if (this.quantity == 0) {
            return this.quantity;
        }

        return this.total / this.quantity;
    }

    getDiscount() {
        if (this.subtotal === null || this.total === null) {
            return null;
        }

        if (this.subtotal == 0) {
            return this.subtotal;
        }

        return ((this.subtotal - this.total) / this.subtotal) * 100;
    }
}

export class DocumentLineItemTransfer extends DataObject {
    _type = "document_line_item_transfer";

    line_item_id;
    line_item;
    document_id;
    document_number;
}

export class DocumentMapperParameter extends Parameter {
    documentMapper;
    documentLineItemMapperParameter = new DocumentLineItemMapperParameter();
    mediaMapperParameter = new MediaMapperParameter();
    companyMapperParameter = new CompanyMapperParameter();
    warehouseMapperParameter = new WarehouseMapperParameter();
}

export class DocumentLineItemMapperParameter extends Parameter {
    documentLineItemMapper;
    documentLineItemTransferMapper;
    productMapperParameter = new ProductMapperParameter();
    taxClassMapperParameter = new TaxClassMapperParameter();
    warehouseMapperParameter = new WarehouseMapperParameter();
}

export class DocumentFactory {
    static bindings = {
        document: Document,
        invoice: InvoiceDocument,
        credit_note: CreditNoteDocument,
        payment: PaymentDocument,
        cash_sale: CashSaleDocument,
        delivery_order: DeliveryOrderDocument,
        loan_invoice: LoanInvoiceDocument,
        loan_payment: LoanPaymentDocument,
        quotation: QuotationDocument,
        sales_order: SalesOrderDocument,
        transfer_note: TransferNoteDocument,
        transfer_request: TransferRequestDocument,
        purchase_order: PurchaseOrderDocument,
        submission: SubmissionDocument,
        proforma_invoice: ProformaInvoiceDocument,
        goods_received: GoodsReceivedDocument,
        stock_issue: StockIssueDocument,
        stock_receive: StockReceiveDocument,
        stock_item_assembly: StockItemAssemblyDocument,
        warranty: WarrantyDocument,
        pick_list: PickListDocument,
        purchase_invoice: PurchaseInvoiceDocument,
        purchase_credit_note: PurchaseCreditNoteDocument,
        debit_note: DebitNoteDocument,
    };

    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {DocumentMapperParameter} documentMapperParameter Parameter object containing mappers that the factory expects.
     * @param {Object, null} data Data to construct the Document object with. Also creates a Meta object using MetaFactory if data is not null.
     * @returns {Document}
     */
    static construct(documentMapperParameter, data = null) {
        // data is the data from the _documentMapper
        if (data != null) {
            let document = DataObjectFactory.getInstance().construct(
                this.bindings[data.type ?? "document"],
                data ? documentMapperParameter.documentMapper(data) : null
            );

            if (data.company) {
                document.company = CompanyFactory.construct(
                    documentMapperParameter.companyMapperParameter,
                    data.company
                );
            }

            if (data.fulfiller) {
                document.fulfiller = CompanyFactory.construct(
                    documentMapperParameter.companyMapperParameter,
                    data.fulfiller
                );
            }

            if (data.to_location != null) {
                document.to_location = WarehouseFactory.construct(
                    documentMapperParameter.warehouseMapperParameter,
                    data.to_location
                );
            }

            if (data.from_location != null) {
                document.from_location = WarehouseFactory.construct(
                    documentMapperParameter.warehouseMapperParameter,
                    data.from_location
                );
            }

            if (data.date_valid_from != null) {
                document.date_valid_from = data.date_valid_from;
            }

            if (data.date_valid_to != null) {
                document.date_valid_to = data.date_valid_to;
            }

            if (data.attachments) {
                const attachments = {};
                data.attachments.forEach((attachment) => {
                    if (attachment.media) {
                        attachments[attachment.media.id] = MediaFactory.construct(
                            documentMapperParameter.mediaMapperParameter,
                            attachment.media
                        );
                    }
                });

                document.attachments = attachments;
            }

            if (data.line_items) {
                const lineItems = {};
                data.line_items.forEach((lineItem) => {
                    lineItems[lineItem.id] = DocumentLineItemFactory.construct(
                        documentMapperParameter.documentLineItemMapperParameter,
                        lineItem
                    );
                });

                document.line_items = lineItems;
            }

            if (document.document_paying_fors) {
                const document_paying_fors = {};
                data.document_paying_fors.forEach((invoice) => {
                    document_paying_fors[invoice.id] = this.construct(documentMapperParameter, invoice);
                    document_paying_fors[invoice.id]["admin_portal_document_id_link"] =
                        invoice.admin_portal_document_id_link ?? null;
                });
                document.document_paying_fors = document_paying_fors;
            }

            if (document.document_paying_withs) {
                const document_paying_withs = {};
                data.document_paying_withs.forEach((creditNote) => {
                    document_paying_withs[creditNote.id] = this.construct(documentMapperParameter, creditNote);
                    document_paying_withs[creditNote.id]["admin_portal_document_id_link"] =
                        creditNote.admin_portal_document_id_link ?? null;
                });
                document.document_paying_withs = document_paying_withs;
            }

            if (data.custom_field_attachments) {
                const custom_field_attachments = {};
                data.custom_field_attachments.forEach((attachments) => {
                    attachments.forEach((attachment) => {
                        custom_field_attachments[attachment.id] = attachment;
                    });
                });
                document.custom_field_attachments = custom_field_attachments;
            }

            if (data.custom_fields) {
                const custom_fields = {};
                data.custom_fields.forEach((customField) => {
                    custom_fields[customField.label] = customField.value;
                });
                document.custom_fields = custom_fields;
            }

            if (data.packed_by) {
                document.packed_by = CompanyFactory.construct(
                    documentMapperParameter.companyMapperParameter,
                    data.packed_by
                );
                document.packed_by_name = document.packed_by.name;
            }

            if (data.proof_of_delivery) {
                const attachments = {};
                data.proof_of_delivery.forEach((attachment) => {
                    if (attachment.media) {
                        attachments[attachment.media.id] = MediaFactory.construct(
                            documentMapperParameter.mediaMapperParameter,
                            attachment.media
                        );
                    }
                });

                document.proof_of_delivery = attachments;
            }

            if (data.received_by_e_signature) {
                const attachments = {};
                data.received_by_e_signature.forEach((attachment) => {
                    if (attachment.media) {
                        attachments[attachment.media.id] = MediaFactory.construct(
                            documentMapperParameter.mediaMapperParameter,
                            attachment.media
                        );
                    }
                });

                document.received_by_e_signature = attachments;
            }

            // AI Document
            if (data.aidoc_attachment) {
                const aidoc_attachment = {};
                data.aidoc_attachment.forEach((attachment) => {
                    if (attachment.media) {
                        aidoc_attachment[attachment.media.id] = MediaFactory.construct(
                            documentMapperParameter.mediaMapperParameter,
                            attachment.media
                        );
                    }
                });

                document.aidoc_attachment = aidoc_attachment;
            }

            // Parcel
            if (data.parcels) {
                const parcels = {};
                data.parcels.forEach((parcelData) => {
                    const parcel = {
                        bo_can_delete: true,
                        show_action_menu: true,
                        id: parcelData.id,
                        tracking_number: parcelData.tracking_number,
                        status: parcelData.status,
                        system_note: parcelData.system_note,
                        attachment: {},
                        tracking_url: parcelData.tracking_url,
                    };
                    if (parcelData.attachment) {
                        parcel.attachment[parcelData.attachment.id] = parcelData.attachment;
                    }
                    parcels[parcel.id] = parcel;
                });

                document.parcels = parcels;
            }

            return document;
        }

        return null;
    }
}
export class DocumentLineItemFactory {
    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {DocumentLineItemMapperParameter} documentLineItemMapperParameter Parameter object containing mappers that the factory expects.
     * @param {Object, null} data Data to construct the DocumentLineItem object with. Also creates a Coupon object if data is not null.
     * @returns {Document}
     */
    static construct(documentLineItemMapperParameter, data = null) {
        if (data != null) {
            let lineItem = DataObjectFactory.getInstance().construct(
                DocumentLineItem,
                data ? documentLineItemMapperParameter.documentLineItemMapper(data) : null
            );

            if (data.product) {
                lineItem.product = ProductFactory.construct(
                    documentLineItemMapperParameter.productMapperParameter,
                    data.product
                );
            }

            if (data.meta) {
                let meta = {};
                data.meta.forEach((metaData) => {
                    meta[metaData.key] = metaData;
                });
                lineItem.meta = meta;
            }

            if (data.transferred_to != null) {
                let temp = {};
                data.transferred_to.forEach((data) => {
                    const model = DataObjectFactory.getInstance().construct(
                        DocumentLineItemTransfer,
                        documentLineItemMapperParameter.documentLineItemTransferMapper(data)
                    );

                    if (data.line_item) {
                        model.line_item = DocumentLineItemFactory.construct(
                            documentLineItemMapperParameter,
                            data.line_item
                        );
                    }

                    temp[model.line_item_id] = model;
                });
                lineItem.transferred_to = temp;
            }

            if (data.tax_class) {
                lineItem.tax_class = TaxClassFactory.construct(
                    documentLineItemMapperParameter.taxClassMapperParameter,
                    data.tax_class
                );
            }

            if (data.from_location != null) {
                lineItem.from_location = WarehouseFactory.construct(
                    documentLineItemMapperParameter.warehouseMapperParameter,
                    data.from_location
                );
            }

            if (data.to_location != null) {
                lineItem.to_location = WarehouseFactory.construct(
                    documentLineItemMapperParameter.warehouseMapperParameter,
                    data.to_location
                );
            }

            return lineItem;
        }

        return null;
    }
}
